import React from 'react'
import ReactDOM from 'react-dom/client'
import { Amplify } from 'aws-amplify'
import 'aws-amplify/auth/enable-oauth-listener'
import * as ppWeb from 'pp-web'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import posthog from 'posthog-js'
import { isPostHogActive } from './providers/posthog-provider'
import { captureAttributionFromUrl } from './utils/utm'
import { configure as configureFlags } from './utils/featureFlags'
import { installWidgetPolnApiFetchShim } from './utils/widgetPolnApiFetchShim.js'
import { deliverMcpConnectResultToOpener } from './utils/mcpConnectPopup'
import { getLocalWidgetBaseUrl } from './utils/localWidgetBaseUrl.js'
import './styles/index.css'

installWidgetPolnApiFetchShim()

// Auth tab finishing OAuth: hand result to the opener and skip booting a second app.
const isMcpConnectPopupReturn = deliverMcpConnectResultToOpener()

const { cfg, getConfig, getServiceProfile } = ppWeb
const NF_EXT_VERSION_MANIFEST_URLS = {
  qa: 'https://s3.us-east-1.amazonaws.com/cdn.getpoln.com/creatorboost/widget/qa/nf-ext-version.json',
  prod: 'https://s3.us-east-1.amazonaws.com/cdn.getpoln.com/creatorboost/widget/nf-ext-version.json'
}

function getNfExtVersionManifestUrl(profile) {
  const normalized = (profile || '').toLowerCase()
  if (normalized === 'qa') {
    return NF_EXT_VERSION_MANIFEST_URLS.qa
  }
  if (normalized === 'local' || normalized === 'dev' || normalized === 'development') {
    return `${getLocalWidgetBaseUrl()}/dist/nf-ext-version.json`
  }
  return NF_EXT_VERSION_MANIFEST_URLS.prod
}

if (import.meta.env?.VITE_DEBUG_CONFIG === '1' && typeof window !== 'undefined') {
  const logResolvedNfConfig = () => ({
    profile: cfg?.profile,
    serviceProfile: getServiceProfile() ?? null,
    polnApiBaseUrl: cfg?.nf?.poln_api_base_url,
    webSocketUrlNf: cfg?.nf?.web_socket_url,
    webSocketUrlPoln: cfg?.poln?.web_socket_url,
    polnConnectApi: cfg?.poln?.connect_api_base_url,
    nfConnectApi: cfg?.nf?.connect_api_base_url,
    kitAppInstallUrl: cfg?.kit?.appInstallUrl,
    kitClientId: cfg?.kit?.clientId,
    extensionId: cfg?.extension?.id,
    siteApi: cfg?.siteApi?.apiUrl || cfg?.siteApi?.baseUrl,
  })

  console.log('[nf:cfg] Resolved config', logResolvedNfConfig())
}

if (typeof window !== 'undefined') {
  window.nf_printConfig = function nf_printConfig() {
    const full = getConfig()
    console.log('[nf:cfg] full pp-web config (getConfig())', full)
    return full
  }
}

// Build Amplify configuration from pp-web config
function getBaseUrl() {
  if (typeof window === 'undefined') {
    // This should never happen in a browser environment
    throw new Error('getBaseUrl() called in non-browser environment. This is a client-side only application.')
  }
  const { protocol, hostname, port } = window.location
  return port ? `${protocol}//${hostname}:${port}` : `${protocol}//${hostname}`
}

function getRedirectPath() {
  return `${getBaseUrl()}`
}

const awsConfig = {
  Auth: {
    Cognito: {
      userPoolId: cfg.nf.user_pool_id,
      userPoolClientId: cfg.nf.user_pool_client_id,
      loginWith: {
        oauth: {
          domain: cfg.nf.user_pool_domain,
          scopes: ['openid', 'email', 'profile', 'phone', 'aws.cognito.signin.user.admin'],
          redirectSignIn: [getRedirectPath()],
          redirectSignOut: [getRedirectPath()],
          responseType: 'code'
        },
        username: 'true',
        email: 'true',
      },
    },
  },
  Storage: {
    S3: {
      bucket: cfg.nf.s3_bucket,
      region: cfg.nf.s3_region,
    }
  }
}

Amplify.configure(awsConfig)

// Stash public-site campaign handoff (utm_* / ph_distinct_id) before OAuth can drop the query.
if (typeof window !== 'undefined' && !isMcpConnectPopupReturn) {
  captureAttributionFromUrl()
}

// Initialize PostHog synchronously before React renders to ensure it's ready before any redirects
if (typeof window !== 'undefined' && !isMcpConnectPopupReturn && isPostHogActive()) {
  try {
    const apiHost = cfg.postHog.reverseProxy || cfg.postHog.host || 'https://us.i.posthog.com'

    posthog.init(cfg.postHog.apiKey, {
      api_host: apiHost,
      person_profiles: 'always',
      capture_pageview: false, // Disable automatic pageview capture, we capture manually
      capture_pageleave: true,
      autocapture: false,
      loaded: () => {
        posthog.register({ site: 'nf' })
      },
    })

    // Capture initial pageview immediately with full URL (including gclid and other params)
    // This ensures we capture the original URL before any redirects happen
    const initialUrl = window.location.origin + window.location.pathname + window.location.search
    posthog.capture('$pageview', { $current_url: initialUrl })

    console.log('✅ PostHog initialized successfully before React render')
  } catch (error) {
    console.error('Failed to initialize PostHog:', error)
  }
}

// Initialize Bugsnag synchronously before React renders to catch early errors
let ErrorBoundary = null
if (cfg?.bugSnag?.apiKey && typeof window !== 'undefined') {
  try {
    // Make Bugsnag available globally for the provider to use
    window.Bugsnag = Bugsnag

    const profile = cfg.profile || 'local'
    const releaseStage =
      profile === 'prod' ? 'production' :
        profile === 'qa' ? 'staging' :
          'development'

    Bugsnag.start({
      apiKey: cfg.bugSnag.apiKey,
      plugins: [new BugsnagPluginReact()],
      releaseStage,
      appVersion: `nf-${process.env.FULL_VERSION}`,
      onError: function (event) {
        const error = event.originalError
        if (
          error?.name === 'ChunkLoadError' ||
          error?.message?.includes("Unexpected token '<'")
        ) {
          if (typeof window !== 'undefined') {
            window.location.reload()
          }
          return false
        } else if (error?.message?.includes('Minified React error')) {
          return false
        }
        return true
      }
    })

    ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React)
    if (ErrorBoundary) {
      console.log('✅ Bugsnag ErrorBoundary created successfully')
    } else {
      console.warn('⚠️ Bugsnag ErrorBoundary not created - check Bugsnag initialization')
    }
  } catch (error) {
    console.error('Failed to initialize Bugsnag:', error)
  }
}

function ErrorView() {
  const handleGoHome = () => {
    window.location.href = '/'
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Oops! Something went wrong
      </h2>
      <p className="text-sm text-gray-600 text-center max-w-md mb-6">
        We track these errors automatically. If the problem persists, please contact support.
      </p>
      <button
        onClick={handleGoHome}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
      >
        Go back to home page
      </button>
    </div>
  )
}

// Initialize app asynchronously to ensure flags are configured before React renders
(async () => {
  if (isMcpConnectPopupReturn) {
    // window.close() was requested; show this only if the browser kept the window open.
    const root = document.getElementById('root')
    if (root) {
      const status = new URLSearchParams(window.location.search).get('mcp_connected')
      root.textContent =
        status === 'success'
          ? 'Connected. You can close this window.'
          : 'You can close this window and return to Newsfiliate.'
    }
    return
  }

  // Configure pp-flags before the first render (components read nf-ext flags synchronously
  // while rendering), but download the App chunk in parallel instead of after the manifest.
  const siteApiUrl = cfg?.siteApi?.apiUrl || cfg?.siteApi?.baseUrl
  const profile = cfg?.profile || 'local'
  const [{ default: App }] = await Promise.all([
    import('./App'),
    configureFlags({
      siteApiBaseUrl: siteApiUrl,
      profile,
      versionManifestUrl: getNfExtVersionManifestUrl(profile)
    })
  ])
  const root = ReactDOM.createRoot(document.getElementById('root'))
  const app = <App />

  root.render(
    <React.StrictMode>
      {ErrorBoundary ? (
        <ErrorBoundary FallbackComponent={ErrorView}>
          {app}
        </ErrorBoundary>
      ) : (
        app
      )}
    </React.StrictMode>
  )
})()
